import React, { useMemo } from 'react';
import {
  View,
  Text,
  ScrollView,
  TouchableOpacity,
  StyleSheet,
  ActivityIndicator,
} from 'react-native';
import {
  Colors,
  FontSize,
  FontWeight,
  Radii,
  Space,
  FontFamily,
} from '../theme/tokens';
import { useResponsive } from '../theme/responsive';
import { useApp, navigate } from '../store/AppStore';
import { Chip } from '../components/Chip';
import { SlaBadge, classifySla } from '../components/SlaState';
import { NodeLabel } from '../components/NodeLabel';
import { Ticket } from '../schema/types';

/**
 * SKTICK-DB-001 Surface A — the assignment-only inbox.
 *
 * WHO THIS IS FOR. Until SKTICK-CR-001, every SK Tickets user was a member of
 * at least one space. WP-1's assignee predicate created a second kind: an
 * outlet manager in a 1,030-outlet franchise network with ZERO space
 * memberships, who sees exactly the tickets individually assigned to them.
 *
 * THE SINGLE RULE THIS SCREEN IS DESIGNED AGAINST:
 *
 *   An assignment-only user must be unable to infer, from any pixel on any
 *   screen, that a ticket exists which they cannot read.
 *
 * So there is deliberately NO space switcher, no space name, no space-scoped
 * count, no org-wide search, no reports, and no total drawn from an unfiltered
 * query. The console's normal chrome assumes a switcher with at least one
 * entry; landing this user on an empty one reads as a broken account AND is
 * itself a small disclosure — an empty space list is different from no space
 * list.
 *
 * Note what is NOT defence here: none of this is access control. RLS already
 * returns only their assignments. This screen's job is to avoid *implying* the
 * rest exists.
 *
 * Mobile matters. Outlet managers open this on a phone, so the list, the
 * detail, commenting, status changes and the deep link must all work at mobile
 * width. The rest of the console may assume a wide viewport; this may not.
 */

const STATUS_TONE: Record<string, any> = {
  open: 'info',
  pending: 'warn',
  resolved: 'success',
  closed: 'neutral',
};
const PRIORITY_TONE: Record<string, any> = {
  urgent: 'danger',
  high: 'danger',
  medium: 'warn',
  low: 'neutral',
};

/**
 * SLA-critical first, then most recent activity.
 *
 * Ordering is by the SERVER's resolved state, never by a locally computed
 * countdown — a paused ticket must not sort as though its clock were running.
 */
const SLA_RANK: Record<string, number> = {
  breached: 0,
  approaching: 1,
  running: 2,
  paused: 3,
  none: 4,
};

function sortForAssignee(a: Ticket, b: Ticket): number {
  const ra = SLA_RANK[classifySla(a.sla)] ?? 4;
  const rb = SLA_RANK[classifySla(b.sla)] ?? 4;
  if (ra !== rb) {
    return ra - rb;
  }
  const ta = a.lastActivityISO ?? a.createdAtISO ?? '';
  const tb = b.lastActivityISO ?? b.createdAtISO ?? '';
  return tb.localeCompare(ta);
}

export function MyAssignmentsScreen() {
  const { visibleTickets, isLoading } = useApp();
  const r = useResponsive();

  /**
   * RLS already narrowed this to the caller's readable rows, and for an
   * assignment-only principal those ARE their assignments. Sorting only.
   *
   * There is no second filter here on purpose: re-filtering client-side would
   * imply the client is the thing deciding, and it is not.
   */
  const rows = useMemo(
    () => [...visibleTickets].sort(sortForAssignee),
    [visibleTickets],
  );

  if (isLoading) {
    return (
      <View style={styles.center}>
        <ActivityIndicator size="large" color={Colors.accent} />
      </View>
    );
  }

  return (
    <ScrollView
      style={styles.screen}
      contentContainerStyle={[
        styles.content,
        { paddingHorizontal: r.pagePadX, paddingVertical: r.pagePadY },
      ]}
    >
      <View style={styles.header}>
        {/* No space name anywhere in this heading — see the rule above. */}
        <Text style={styles.title}>My assignments</Text>
        {rows.length > 0 && (
          // A count of THEIR OWN rows only. Never a space total, and never a
          // number derived from an unfiltered query.
          <Text style={styles.subtitle}>
            {`${rows.length} ${
              rows.length === 1 ? 'ticket' : 'tickets'
            } assigned to you`}
          </Text>
        )}
      </View>

      {rows.length === 0 ? (
        <View style={styles.empty}>
          {/*
            Calm, complete, not an error. Never "you don't have access to any
            space" — that names a thing they cannot see. And no "browse" or
            "request access" affordance, which would imply there is more.
          */}
          <Text style={styles.emptyText}>
            Nothing is assigned to you right now.
          </Text>
        </View>
      ) : (
        <View style={styles.list}>
          {rows.map(t => {
            const apiId = (t as any)._apiId ?? t.id;
            return (
              <TouchableOpacity
                key={t.id}
                style={styles.row}
                accessibilityRole="button"
                onPress={() => navigate(`/tickets/${apiId}`)}
              >
                <View style={styles.rowTop}>
                  <Text style={styles.subject} numberOfLines={2}>
                    {t.subject}
                  </Text>
                  <Text style={styles.ref}>{t.id}</Text>
                </View>

                <View style={styles.rowMeta}>
                  {/* Outlet label — a label, never a link, never a breadcrumb. */}
                  <NodeLabel nodeId={t.nodeId} nodeLabel={t.nodeLabel} />
                  <Chip
                    label={
                      t.priority.charAt(0).toUpperCase() + t.priority.slice(1)
                    }
                    tone={PRIORITY_TONE[t.priority] ?? 'neutral'}
                    dot
                  />
                  <Chip
                    label={t.status.charAt(0).toUpperCase() + t.status.slice(1)}
                    tone={STATUS_TONE[t.status] ?? 'neutral'}
                    dot
                  />
                  {/* Server-resolved. A paused clock renders visibly stopped. */}
                  <SlaBadge sla={t.sla} />
                  {typeof t.reopenCount === 'number' && t.reopenCount >= 1 && (
                    <Chip label={`Reopened ${t.reopenCount}×`} tone="warn" />
                  )}
                </View>

                <Text style={styles.activity}>
                  {t.lastActivityAgo ?? t.openedAgo}
                </Text>
              </TouchableOpacity>
            );
          })}
        </View>
      )}
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  screen: { flex: 1, backgroundColor: Colors.paper },
  center: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: Colors.paper,
  },
  // Comfortable to read on a phone, capped so it does not sprawl on a desktop.
  content: { gap: Space[4], maxWidth: 820, width: '100%', alignSelf: 'center' },
  header: { gap: Space[1] },
  title: {
    fontSize: FontSize['2xl'],
    fontWeight: FontWeight.semibold,
    color: Colors.ink,
    fontFamily: FontFamily.sans,
  },
  subtitle: {
    fontSize: FontSize.md,
    color: Colors.ink3,
    fontFamily: FontFamily.sans,
  },
  empty: {
    paddingVertical: Space[12],
    alignItems: 'center',
  },
  emptyText: {
    fontSize: FontSize.lg,
    color: Colors.ink3,
    fontFamily: FontFamily.sans,
    textAlign: 'center',
  },
  list: { gap: Space[3] },
  row: {
    gap: Space[2],
    padding: Space[4],
    backgroundColor: Colors.card,
    borderRadius: Radii.r3,
    borderWidth: 1,
    borderColor: Colors.line,
    // Generous target: this is a phone surface first.
    minHeight: 72,
  },
  rowTop: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: Space[3],
  },
  subject: {
    flex: 1,
    fontSize: FontSize.lg,
    fontWeight: FontWeight.medium,
    color: Colors.ink,
    fontFamily: FontFamily.sans,
    lineHeight: 21,
  },
  ref: {
    fontSize: FontSize.xs,
    color: Colors.ink4,
    fontFamily: FontFamily.mono,
  },
  rowMeta: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: Space[2],
  },
  activity: {
    fontSize: FontSize.xs,
    color: Colors.ink4,
    fontFamily: FontFamily.sans,
  },
});
